import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import { PlusIcon } from "@lucide/icons";

import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import type {
  LinkChartDataFn,
  ShortenedLink,
  ShortenedUrlsQueryParams,
} from "../types";
import LinkRow from "./LinkRow";
import { ShortLinkCreator } from "./Links";

type ManageLinksModalProps = {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

const ManageLinksModal = ({
  isOpen,
  onOpen = () => {},
  onClose = () => {},
}: ManageLinksModalProps) => {
  const [links, setLinks] = useState<ShortenedLink[]>([]);
  const [hasLinksResults, setHasLinksResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const listQuery = useMemo<ShortenedUrlsQueryParams>(
    () => ({ page: 1, limit: 100 }),
    [],
  );
  const [creatingNewLink, setCreatingNewLink] = useState(false);
  const [newLinkState, setNewLinkState] = useState<{
    url: string;
    path: string;
    description: string;
  }>({ url: "", path: "", description: "" });
  const [creatingNewLinkLoading, setCreatingNewLinkLoading] = useState(false);
  const user = useAppSelector((state) => state.user);
  const orgId = user?.ownerOrganisation?._id;
  const emptyChartData = useCallback<LinkChartDataFn>(() => null, []);

  const getShortenedUrls = useCallback(() => {
    if (!orgId) return;

    setLoading(true);
    api.organisations.getShortenedUrls(
      orgId,
      listQuery,
      (data) => {
        setLinks(data.shortenedUrls);
        setHasLinksResults((prev) => prev || data.shortenedUrls.length > 0);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
  }, [orgId, listQuery]);

  const filteredLinks = useMemo(() => {
    if (!searchQuery) return links;

    const query = searchQuery.toLowerCase();
    return links.filter(
      (link) =>
        link.path.toLowerCase().includes(query) ||
        link.url.toLowerCase().includes(query),
    );
  }, [links, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      getShortenedUrls();
    }
  }, [getShortenedUrls, isOpen]);

  const createUrl = useCallback(() => {
    if (!orgId) return;

    setCreatingNewLinkLoading(true);
    api.organisations.createShortenedUrl(
      orgId,
      newLinkState,
      (shortenedUrl) => {
        setNewLinkState({ url: "", path: "", description: "" });
        setCreatingNewLinkLoading(false);
        setCreatingNewLink(false);
        setLinks((prevLinks) => [shortenedUrl, ...prevLinks]);
        setHasLinksResults(true);
      },
      (err) => {
        setCreatingNewLinkLoading(false);
        toast.error(err.message);
      },
    );
  }, [orgId, newLinkState]);

  const deleteUrl = useCallback(
    (urlId: string) => {
      if (!orgId) return;

      util
        .confirm(
          "Really delete this URL?",
          "The URL and associated analytics data will be immediately removed. Be careful - someone else can take your short URL once you delete it.",
        )
        .then(() => {
          api.organisations.deleteShortenedUrl(
            orgId,
            urlId,
            () => {
              setLinks((prevLinks) => {
                const nextLinks = prevLinks.filter(
                  (link) => link._id !== urlId,
                );
                if (nextLinks.length === 0) {
                  setHasLinksResults(false);
                }
                return nextLinks;
              });
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [orgId],
  );

  const copyLink = useCallback((link: string) => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard
        .writeText(`${sdiClickDomain}/${link}`)
        .then(() => toast.success("Link copied to your clipboard"))
        .catch(() => toast.error("Failed to copy link to clipboard"));
    } else {
      toast.error("Your browser does not support copying");
    }
  }, []);

  return (
    <Modal open={isOpen} onClose={onClose} onOpen={onOpen}>
      <ModalHeader>
        {user?.ownerOrganisation?.name}&apos;s Shortened Links
      </ModalHeader>
      <ModalContent scrolling>
        {loading && !hasLinksResults ? <Loader /> : null}
        {!creatingNewLink ? (
          <Button
            fluid
            onClick={() => setCreatingNewLink(true)}
            style={{ marginBottom: 10 }}
            color="primary"
          >
            <Icon icon={PlusIcon} />
            Create new shortened link
          </Button>
        ) : (
          <>
            <ShortLinkCreator
              url={newLinkState.url}
              path={newLinkState.path}
              description={newLinkState.description}
              setUrl={(url) => setNewLinkState({ ...newLinkState, url })}
              setPath={(path) => setNewLinkState({ ...newLinkState, path })}
              setDescription={(description) =>
                setNewLinkState({ ...newLinkState, description })
              }
            />
            <Button
              onClick={createUrl}
              loading={creatingNewLinkLoading}
              style={{ marginTop: 10 }}
              color="primary"
              disabled={!newLinkState.url || !newLinkState.path}
            >
              <Icon icon={PlusIcon} />
              Create shortened link using these details
            </Button>
          </>
        )}
        {hasLinksResults ? (
          <>
            <Divider section />
            <Input
              fluid
              placeholder="Search by URL or path"
              loading={loading}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Link</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Total clicks</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLinks.map((link) => (
                  <LinkRow
                    key={link._id}
                    link={link}
                    chartData={emptyChartData}
                    copyLink={copyLink}
                    deleteUrl={deleteUrl}
                    disableReferrals
                  />
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}
        {!loading && links.length === 0 ? (
          <EmptyBox title="No shortened links found" />
        ) : null}
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Done</Button>
      </ModalActions>
    </Modal>
  );
};

export default ManageLinksModal;
